import React from "react";
import { Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { formatMinutesToHHMM } from "../../../../utils/dateUtils";
import { WarningOutlined, ClockCircleOutlined } from "@ant-design/icons";
import {
    computeStaFromStdAndBlockTime,
    getCurrentBlockTimeMinutes,
    hasExpiredStaManual,
    isManualStaStillValid,
    isStaLeak,
    parsePositiveMinutes,
    truncateIsoToMinute,
} from "../../../../utils/staBlockTimeAlignment";

dayjs.extend(utc);

/**
 * 显示服务 - 处理航班表格UI相关的渲染逻辑
 */
class DisplayService {
    /**
     * 生成表格行样式
     * @param {Object} record - 行数据
     * @param {Set} transportOrigins - 运输需求始发地集合
     * @returns {string} 类名字符串
     */
    static getRowClassName(record, transportOrigins) {
        // 跳过特殊行
        if (record.isAddHeaderRow || record.isAddRow) {
            return "";
        }

        let classNames = [];

        // 检查是否是轮换的航班
        if (record.rotation_no) {
            classNames.push("rotation-flight");

            // 判断开始和结束的新逻辑：
            // 1. 从运输需求始发地起飞 = 开始
            // 2. 在运输需求始发地落地 = 结束
            // 3. 既从始发地起飞又在始发地落地 = 特殊标记
            const startsAtOrigin = transportOrigins.has(record.departure_airport);
            const endsAtOrigin = transportOrigins.has(record.arrival_airport);

            if (startsAtOrigin && endsAtOrigin) {
                // 特殊情况：一个航班既是开始又是结束
                classNames.push("rotation-start");
                classNames.push("rotation-end");
            } else if (startsAtOrigin) {
                // 从运输需求始发地起飞的航班是轮换开始
                classNames.push("rotation-start");
            } else if (endsAtOrigin) {
                // 在运输需求始发地落地的航班是轮换结束
                classNames.push("rotation-end");
            } else {
                // 其他航班是轮换中间段
                classNames.push("rotation-middle");
            }

            // 标记轮换是否完整（用于颜色区分）
            if (record.is_rotation_complete === false) {
                classNames.push("rotation-incomplete");
            }
        }

        return classNames.join(" ");
    }

    /**
     * 渲染时间调整详情
     * @param {Array} adjustments - 调整记录数组
     * @returns {JSX.Element} React组件
     */
    static renderTimeAdjustmentsDetail(adjustments) {
        if (!adjustments || adjustments.length === 0) {
            return null;
        }

        // 计算总调整时间
        const totalMinutes = adjustments.reduce((total, adj) => total + adj.timeDiff, 0);
        const totalHours = Math.floor(totalMinutes / 60);
        const remainingMinutes = totalMinutes % 60;

        let totalTimeDesc = "";
        if (totalHours > 0 && remainingMinutes > 0) {
            totalTimeDesc = `${totalHours}小时${remainingMinutes}分钟`;
        } else if (totalHours > 0) {
            totalTimeDesc = `${totalHours}小时`;
        } else {
            totalTimeDesc = `${remainingMinutes}分钟`;
        }

        return (
            <div style={{ maxWidth: "400px" }}>
                <div style={{ marginBottom: "8px", fontWeight: "bold" }}>时间调整记录（总计: {totalTimeDesc}）</div>
                <ul style={{ paddingLeft: "20px", margin: 0 }}>
                    {adjustments.map((adj, index) => (
                        <li key={index} style={{ marginBottom: "8px" }}>
                            <div>
                                <strong>调整 {index + 1}:</strong> {adj.timeDesc}
                            </div>
                            <div>原因: {adj.reason}</div>
                            {adj.detail && adj.detail !== adj.reason && <div>详情: {adj.detail}</div>}
                            <div style={{ color: "#888" }}>
                                从 {adj.from} → 到 {adj.to}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    /**
     * 渲染宵禁违反标签
     * @param {Array} violations - 违反记录数组
     * @param {string} type - 违反类型 (departure_curfew 或 arrival_curfew)
     * @returns {JSX.Element|null} React组件
     */
    static renderCurfewViolationTag(violations, type) {
        if (!violations || violations.length === 0) return null;

        const error = violations.find((v) => v.severity === "error" && v.violationTypes && v.violationTypes.includes(type));
        const warning = violations.find((v) => v.severity === "warning" && v.violationTypes && v.violationTypes.includes(`${type}_warning`));
        const notice = violations.find((v) => v.severity === "notice" && v.violationTypes && v.violationTypes.includes(`${type}_notice`));

        if (error) {
            // 查找消息索引
            const msgIndex = error.violationTypes ? error.violationTypes.findIndex((t) => t === type) : 0;
            return (
                <Tooltip title={error.messages && error.messages[msgIndex >= 0 ? msgIndex : 0]}>
                    <Tag color="red" style={{ marginLeft: 4 }}>
                        <WarningOutlined /> 宵禁
                    </Tag>
                </Tooltip>
            );
        }

        if (warning) {
            // 查找警告消息索引
            const msgIndex = warning.violationTypes ? warning.violationTypes.findIndex((t) => t === `${type}_warning`) : 0;
            return (
                <Tooltip title={warning.messages && warning.messages[msgIndex >= 0 ? msgIndex : 0]}>
                    <Tag color="orange" style={{ marginLeft: 4 }}>
                        {warning.minutesToCurfew && msgIndex >= 0 && warning.minutesToCurfew[msgIndex] !== undefined ? warning.minutesToCurfew[msgIndex] : warning.minutesToCurfew && warning.minutesToCurfew[0]} min
                    </Tag>
                </Tooltip>
            );
        }

        if (notice) {
            // 查找通知消息索引
            const msgIndex = notice.violationTypes ? notice.violationTypes.findIndex((t) => t === `${type}_notice`) : 0;
            return (
                <Tooltip title={notice.messages && notice.messages[msgIndex >= 0 ? msgIndex : 0]}>
                    <Tag color="blue" style={{ marginLeft: 4 }}>
                        {notice.minutesToCurfew && msgIndex >= 0 && notice.minutesToCurfew[msgIndex] !== undefined ? notice.minutesToCurfew[msgIndex] : notice.minutesToCurfew && notice.minutesToCurfew[0]} min
                    </Tag>
                </Tooltip>
            );
        }

        return null;
    }

    /**
     * 渲染地面时间
     * @param {number} actualGroundTime - 实际地面时间(分钟)
     * @param {number} minGroundTime - 最小地面时间(分钟)
     * @param {Object} groundTimeViolation - 地面时间违反信息
     * @returns {JSX.Element} React组件
     */
    static renderGroundTime(actualGroundTime, minGroundTime, groundTimeViolation) {
        // 0 分钟是合法过站（机场明确配成 0 时，后一班紧接着前一班起飞）。
        // 不能用真假值判断，否则格子会空白，看起来像没算出来。
        if (typeof actualGroundTime !== "number" || !Number.isFinite(actualGroundTime)) return "";
        const hasMinGroundTime = typeof minGroundTime === "number" && Number.isFinite(minGroundTime);

        const formattedTime = formatMinutesToHHMM(actualGroundTime);
        const ratio = hasMinGroundTime && minGroundTime > 0 ? actualGroundTime / minGroundTime : 1;
        const minTimeColor = "#52c41a"; // 绿色表示最小地面时间
        const exceededTimeColor = ratio < 1 ? "#ff4d4f" : "#faad14"; // 红/黄

        // 计算百分比宽度
        let minTimePercent, exceededPercent;

        if (!hasMinGroundTime) {
            minTimePercent = 0;
            exceededPercent = 100;
        } else if (actualGroundTime < minGroundTime) {
            minTimePercent = 0;
            exceededPercent = 100;
        } else if (actualGroundTime === 0) {
            minTimePercent = 100;
            exceededPercent = 0;
        } else {
            minTimePercent = (minGroundTime / actualGroundTime) * 100;
            exceededPercent = ((actualGroundTime - minGroundTime) / actualGroundTime) * 100;
        }

        return (
            <span>
                <Tooltip
                    title={
                        <div>
                            <div>最小地面时间: {hasMinGroundTime ? formatMinutesToHHMM(minGroundTime) : "未设置"}</div>
                            <div>实际地面时间: {formattedTime}</div>
                            {hasMinGroundTime && actualGroundTime > minGroundTime && <div>超出: {formatMinutesToHHMM(actualGroundTime - minGroundTime)}</div>}
                            {hasMinGroundTime && actualGroundTime < minGroundTime && <div>不足: {formatMinutesToHHMM(minGroundTime - actualGroundTime)}</div>}
                        </div>
                    }
                >
                    <div style={{ display: "block" }}>
                        <div>{formattedTime}</div>
                        {actualGroundTime > 0 && (
                            <div style={{ width: "100%", height: "2px", background: "#f0f0f0", marginTop: "2px", display: "flex" }}>
                                {hasMinGroundTime && actualGroundTime < minGroundTime ? (
                                    // 如果实际时间小于最小要求，显示100%红色
                                    <div
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            background: "#ff4d4f", // 红色表示不足
                                        }}
                                    />
                                ) : (
                                    <>
                                        {hasMinGroundTime && minGroundTime > 0 && (
                                            <div
                                                style={{
                                                    width: `${minTimePercent}%`,
                                                    height: "100%",
                                                    background: minTimeColor,
                                                }}
                                            />
                                        )}
                                        <div
                                            style={{
                                                width: `${exceededPercent}%`,
                                                height: "100%",
                                                background: exceededTimeColor,
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </Tooltip>
                {groundTimeViolation && (
                    <Tooltip
                        title={
                            <>
                                <div>{groundTimeViolation.message || "地面时间不足"}</div>
                                {groundTimeViolation.actualTime !== undefined && groundTimeViolation.requiredTime !== undefined && (
                                    <div>
                                        实际: {formatMinutesToHHMM(groundTimeViolation.actualTime)}, 要求: {formatMinutesToHHMM(groundTimeViolation.requiredTime)}
                                    </div>
                                )}
                            </>
                        }
                    >
                        <Tag color="orange" style={{ marginLeft: 4 }}>
                            <ClockCircleOutlined />
                            {groundTimeViolation.requiredTime !== undefined && groundTimeViolation.actualTime !== undefined ? `缺 ${Math.ceil(Math.max(0, groundTimeViolation.requiredTime - groundTimeViolation.actualTime))}分钟` : "不足"}
                        </Tag>
                    </Tooltip>
                )}
            </span>
        );
    }

    /**
     * 渲染飞行时间(轮档时间)
     * @param {Object} record - 航班记录
     * @returns {JSX.Element} React组件
     */
    static renderBlockTime(record) {
        const actualBlockTime = getCurrentBlockTimeMinutes(record);
        const minBlockTime = parsePositiveMinutes(record.min_block_time_minutes) ?? actualBlockTime;

        if (!actualBlockTime) return "";

        const formattedTime = formatMinutesToHHMM(actualBlockTime);

        // 计算性能指标：实际时间与最小时间的比率
        const ratio = minBlockTime ? actualBlockTime / minBlockTime : 1;

        // 基础颜色定义
        const minTimeColor = "#52c41a"; // 绿色表示最小轮档时间
        const exceededTimeColor = ratio > 1 ? "#faad14" : "#ff4d4f"; // 黄色表示超出，红色表示不足

        // 修改百分比宽度逻辑
        let minTimePercent, exceededPercent;

        if (!minBlockTime) {
            // 如果没有最小轮档时间要求，显示100%绿色
            minTimePercent = 0;
            exceededPercent = 100;
        } else if (actualBlockTime < minBlockTime) {
            // 如果实际时间小于最小要求，显示100%红色
            minTimePercent = 0;
            exceededPercent = 100;
        } else {
            // 如果实际时间大于最小要求，则最小要求部分为绿色，超出部分为黄色
            minTimePercent = (minBlockTime / actualBlockTime) * 100;
            exceededPercent = ((actualBlockTime - minBlockTime) / actualBlockTime) * 100;
        }

        return (
            <Tooltip
                title={
                    <div>
                        <div>最小轮档时间: {formatMinutesToHHMM(minBlockTime || 0)}</div>
                        <div>实际轮档时间: {formattedTime}</div>
                        {ratio > 1 && <div>超出: {formatMinutesToHHMM(actualBlockTime - (minBlockTime || 0))}</div>}
                        {ratio < 1 && <div>不足: {formatMinutesToHHMM(minBlockTime - actualBlockTime)}</div>}
                    </div>
                }
            >
                <div style={{ display: "block" }}>
                    <div>{formattedTime}</div>
                    {minBlockTime && (
                        <div style={{ width: "100%", height: "2px", background: "#f0f0f0", marginTop: "2px", display: "flex" }}>
                            {minBlockTime && actualBlockTime < minBlockTime ? (
                                <div
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        background: "#ff4d4f",
                                    }}
                                />
                            ) : (
                                <>
                                    {minBlockTime > 0 && (
                                        <div
                                            style={{
                                                width: `${minTimePercent}%`,
                                                height: "100%",
                                                background: minTimeColor,
                                            }}
                                        />
                                    )}
                                    <div
                                        style={{
                                            width: `${exceededPercent}%`,
                                            height: "100%",
                                            background: exceededTimeColor,
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>
            </Tooltip>
        );
    }

    /**
     * 预落旁提示：漏改写出按轮档该是几点；仍有效手填标「手填，比轮档差 N 分」。
     */
    static renderStaFollowHint(record) {
        const blockTimeMinutes = getCurrentBlockTimeMinutes(record);
        const expectedSta = computeStaFromStdAndBlockTime(record?.std, blockTimeMinutes);
        if (!expectedSta || !truncateIsoToMinute(record?.sta)) return null;
        if (!isStaLeak(record) && !isManualStaStillValid(record)) return null;

        const deltaMinutes = Math.abs(dayjs.utc(truncateIsoToMinute(record.sta)).diff(dayjs.utc(truncateIsoToMinute(expectedSta)), "minute"));
        if (deltaMinutes === 0) {
            if (hasExpiredStaManual(record)) {
                return (
                    <Tooltip title="记下的轮档已过期。到达碰巧已对齐，打开或保存时仍需确认，避免手填标记以后复活。">
                        <Tag style={{ marginLeft: 4, marginTop: 2 }}>过期手填</Tag>
                    </Tooltip>
                );
            }
            return null;
        }

        const expectedClock = dayjs.utc(expectedSta).format("HH:mm");
        if (isManualStaStillValid(record)) {
            return (
                <Tooltip title={`手填到达，比当前轮档差 ${deltaMinutes} 分`}>
                    <Tag style={{ marginLeft: 4, marginTop: 2 }}>手填，差 {deltaMinutes} 分</Tag>
                </Tooltip>
            );
        }

        return (
            <Tooltip title={`按轮档该是 ${expectedClock}（UTC），差 ${deltaMinutes} 分`}>
                <Tag color="warning" style={{ marginLeft: 4, marginTop: 2 }}>
                    该 {expectedClock}，差 {deltaMinutes} 分
                </Tag>
            </Tooltip>
        );
    }

    /**
     * 渲染轮换单元格内容
     * @param {Object} record - 航班记录
     * @param {Array} filteredFlights - 过滤后的航班列表
     * @param {Set} transportOrigins - 运输需求始发地集合
     * @returns {JSX.Element|string} React组件或字符串
     */
    static renderRotationCell(record, filteredFlights, transportOrigins) {
        const text = record.rotation_no;
        if (!text) return "-";

        // 检查轮换是否完成，决定颜色
        let isComplete = record.is_rotation_complete;
        if (isComplete === undefined || isComplete === null) {
            isComplete = text.startsWith("ROT-");
        }
        const rotationColor = isComplete ? "#1890ff" : "#faad14"; // 蓝色表示完成，黄色表示未完整

        // 判断开始和结束的简化逻辑：
        // 1. 从运输需求始发地起飞 = 开始
        // 2. 在运输需求始发地落地 = 结束
        // 3. 既从始发地起飞又在始发地落地 = 特殊标记
        const startsAtOrigin = transportOrigins.has(record.departure_airport);
        const endsAtOrigin = transportOrigins.has(record.arrival_airport);

        // 定义U型边框样式
        let borderStyle = {};
        let containerStyle = {
            height: "42px", // 固定高度
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
        };

        if (startsAtOrigin && endsAtOrigin) {
            // 特殊情况：一个航班既是开始又是结束
            borderStyle = {
                border: `2px solid ${rotationColor}`,
                width: "20px",
                height: "36px",
                borderRadius: "50%", // 圆形
            };
        } else if (startsAtOrigin) {
            // 从运输需求始发地起飞的航班是轮换开始 - 倒U型，放在底部
            containerStyle.justifyContent = "flex-end";
            borderStyle = {
                borderLeft: `2px solid ${rotationColor}`,
                borderTop: `2px solid ${rotationColor}`,
                borderRight: `2px solid ${rotationColor}`,
                borderBottom: "none",
                width: "20px",
                height: "36px",
                borderRadius: "4px 4px 0 0",
            };
        } else if (endsAtOrigin) {
            // 在运输需求始发地落地的航班是轮换结束 - 正U型，放在顶部
            containerStyle.justifyContent = "flex-start";
            borderStyle = {
                borderLeft: `2px solid ${rotationColor}`,
                borderBottom: `2px solid ${rotationColor}`,
                borderRight: `2px solid ${rotationColor}`,
                borderTop: "none",
                width: "20px",
                height: "36px",
                borderRadius: "0 0 4px 4px",
            };
        } else {
            // 其他航班是轮换中间段 - 两条竖线，占满高度
            borderStyle = {
                borderLeft: `2px solid ${rotationColor}`,
                borderRight: `2px solid ${rotationColor}`,
                borderTop: "none",
                borderBottom: "none",
                width: "20px",
                height: "100%",
                position: "absolute",
            };
        }

        // 获取相同轮换的所有航班（用于工具提示显示）
        const rotationFlights = filteredFlights.filter((f) => f.rotation_no === text);
        // 构建路径字符串
        const routePath =
            rotationFlights.length > 0
                ? rotationFlights
                      .map((f) => f.departure_airport)
                      .concat([record.arrival_airport])
                      .join("-")
                : `${record.departure_airport}-${record.arrival_airport}`;

        // 轮换编号: ROT-20250402-2   取最后一位
        const rotationNumber = text.split("-").pop();
        return (
            <Tooltip
                title={
                    <div>
                        <div>
                            轮换编号: {text} ({isComplete ? "完整" : "未完成"})
                        </div>
                        <div>路径: {routePath}</div>
                    </div>
                }
            >
                <div style={{ textAlign: "center" }}>
                    <div style={{ ...containerStyle, position: "relative" }}>
                        <div style={borderStyle}></div>
                        <span
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                fontSize: "12px",
                            }}
                        >
                            {rotationNumber}
                        </span>
                    </div>
                </div>
            </Tooltip>
        );
    }
}

export default DisplayService;
